import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import classroomService from '../../services/classroomService';
import classroomForumService from '../../services/classroomForumService';
import classroomTaskService from '../../services/classroomTaskService';
import classroomExamService from '../../services/classroomExamService';
import documentService from '../../services/documentService';
import utils from '../../utils/generalFunctions';
import types from '../../services/types';
import HtmlEditor from '../HtmlEditor';

// Crea un recurso nuevo (y, si el tipo lo requiere, la entidad de fondo:
// video/foro/tarea/examen) dentro de una sección, o edita los campos de
// colocación (name/description/orden/isVisible) de uno existente — el
// contenido propio de tarea/examen/foro/video se edita en su propia
// pantalla de gestión, no aquí (ver README del backend §4.1).
const ClassroomResourceForm = ({ classroomId, sectionId, resource, onClose }) => {
    const esNuevo = !resource;
    const [tipo, setTipo] = useState(resource?.resourceTypeId || types.classroomResourceTypes.CONTENT);
    const [creado, setCreado] = useState(null); // { examId } cuando se crea un examen, para ofrecer el link a configurarlo

    const [placement, setPlacement] = useState({
        name: resource?.name || '',
        description: resource?.description || '',
        orden: resource?.orden ?? 0,
        isVisible: resource?.isVisible ?? false
    });

    const [contentHtml, setContentHtml] = useState(resource?.contentHtml || '');
    const [externalUrl, setExternalUrl] = useState(resource?.externalUrl || '');
    const [file, setFile] = useState(null);

    const [video, setVideo] = useState({ providerId: types.classroomVideoProvider.YOUTUBE, title: '', videoUrl: '', descriptionHtml: '' });
    const [forum, setForum] = useState({ title: '', descriptionHtml: '', allowAttachments: true });
    const [task, setTask] = useState({ taskTypeId: types.classroomTaskTypes.TAREA, title: '', instructionsHtml: '', openDate: '', dueDate: '', allowLateSubmission: false, maxScore: 5 });
    const [examTitle, setExamTitle] = useState('');

    const handlePlacementChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;
        setPlacement((prev) => ({ ...prev, [name]: inputType === 'checkbox' ? checked : value }));
    };

    const buildResourcePayload = (extra) => {
        const user = utils.getUserInformation();
        return {
            sectionId,
            resourceTypeId: tipo,
            name: placement.name || null,
            description: placement.description || null,
            orden: placement.orden,
            isVisible: placement.isVisible,
            createdUser: user?.userId,
            modificationUser: user?.userId,
            ...extra
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const user = utils.getUserInformation();

        try {
            // Editar solo la colocación de un recurso ya existente.
            if (!esNuevo) {
                const guardado = await classroomService.updateResource({ ...resource, ...placement, modificationUser: user?.userId });
                Swal.fire('Listo', 'Recurso actualizado con éxito.', 'success');
                if (onClose) onClose(guardado);
                return;
            }

            let extra = {};
            if (tipo === types.classroomResourceTypes.CONTENT) {
                if (!placement.name || !contentHtml) {
                    Swal.fire('Campos obligatorios', 'El nombre y el contenido son obligatorios.', 'error');
                    return;
                }
                extra = { contentHtml };
            } else if (tipo === types.classroomResourceTypes.LINK) {
                if (!placement.name || !externalUrl) {
                    Swal.fire('Campos obligatorios', 'El nombre y la URL son obligatorios.', 'error');
                    return;
                }
                extra = { externalUrl };
            } else if (tipo === types.classroomResourceTypes.FILE) {
                if (!file) {
                    Swal.fire('Campo obligatorio', 'Selecciona un archivo.', 'error');
                    return;
                }
                const doc = await documentService.addDocument(
                    types.documentTypes.CLASSROOM_RESOURCE_FILE,
                    file,
                    sectionId,
                    file.name,
                    file.size,
                    file.type
                );
                extra = { documentId: doc.documentId };
            } else if (tipo === types.classroomResourceTypes.VIDEO) {
                if (!video.title || !video.videoUrl) {
                    Swal.fire('Campos obligatorios', 'El título y la URL del video son obligatorios.', 'error');
                    return;
                }
                const created = await classroomService.createVideo({ ...video, classroomId, createdUser: user?.userId, modificationUser: user?.userId });
                extra = { videoId: created.videoId };
            } else if (tipo === types.classroomResourceTypes.FORUM) {
                if (!forum.title) {
                    Swal.fire('Campo obligatorio', 'El título del foro es obligatorio.', 'error');
                    return;
                }
                const created = await classroomForumService.createForum({ ...forum, classroomId, createdUser: user?.userId, modificationUser: user?.userId });
                extra = { forumId: created.forumId };
            } else if (tipo === types.classroomResourceTypes.TASK) {
                if (!task.title) {
                    Swal.fire('Campo obligatorio', 'El título de la tarea es obligatorio.', 'error');
                    return;
                }
                const created = await classroomTaskService.createTask({ ...task, classroomId, createdUser: user?.userId, modificationUser: user?.userId });
                extra = { taskId: created.taskId };
            } else if (tipo === types.classroomResourceTypes.EXAM) {
                if (!examTitle) {
                    Swal.fire('Campo obligatorio', 'El título del examen es obligatorio.', 'error');
                    return;
                }
                const created = await classroomExamService.createExam({ classroomId, title: examTitle, createdUser: user?.userId, modificationUser: user?.userId });
                extra = { examId: created.examId };
            }

            const payload = buildResourcePayload(extra);
            const guardado = await classroomService.createResource(payload);

            if (tipo === types.classroomResourceTypes.EXAM) {
                setCreado({ examId: extra.examId, resource: guardado });
            } else {
                Swal.fire('Listo', 'Recurso agregado con éxito.', 'success');
                if (onClose) onClose(guardado);
            }
        } catch (error) {
            console.error(error);
            Swal.fire('Ha ocurrido un error', error?.response?.data?.message || 'Por favor intentelo más tarde', 'error');
        }
    };

    if (creado) {
        return (
            <div className="container-fluid mt-2">
                <div className="alert alert-success">Examen creado. Ahora configura sus preguntas, tiempo y ponderación.</div>
                <Link to={`/aula/examen/${creado.examId}`} className="btn btn-primary me-2">Configurar examen</Link>
                <button className="btn btn-outline-secondary" onClick={() => onClose && onClose(creado.resource)}>Cerrar</button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="container-fluid mt-2">
            {esNuevo && (
                <div className="mb-3">
                    <label className="form-label">Tipo de recurso *</label>
                    <select className="form-control form-select" value={tipo} onChange={(e) => setTipo(Number(e.target.value))}>
                        <option value={types.classroomResourceTypes.CONTENT}>Contenido (HTML)</option>
                        <option value={types.classroomResourceTypes.FILE}>Archivo</option>
                        <option value={types.classroomResourceTypes.VIDEO}>Video embebido</option>
                        <option value={types.classroomResourceTypes.FORUM}>Foro de discusión</option>
                        <option value={types.classroomResourceTypes.TASK}>Tarea / actividad / taller</option>
                        <option value={types.classroomResourceTypes.EXAM}>Examen</option>
                        <option value={types.classroomResourceTypes.LINK}>Enlace externo</option>
                    </select>
                </div>
            )}

            {!esNuevo && (
                <div className="alert alert-secondary">
                    Tipo: <strong>{resource.resourceTypeName}</strong>. Para editar el contenido propio (título, preguntas, fechas, etc.), entra a gestionarlo desde el recurso ya colocado.
                </div>
            )}

            {(esNuevo && (tipo === types.classroomResourceTypes.CONTENT || tipo === types.classroomResourceTypes.LINK)) && (
                <div className="mb-3">
                    <label className="form-label">Nombre *</label>
                    <input type="text" className="form-control" name="name" value={placement.name} onChange={handlePlacementChange} required />
                </div>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.CONTENT && (
                <div className="mb-3">
                    <label className="form-label">Contenido *</label>
                    <HtmlEditor value={contentHtml} onChange={setContentHtml} placeholder="Escribe el contenido para los estudiantes..." />
                </div>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.LINK && (
                <div className="mb-3">
                    <label className="form-label">URL *</label>
                    <input type="url" className="form-control" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://..." required />
                </div>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.FILE && (
                <div className="mb-3">
                    <label className="form-label">Archivo *</label>
                    <input type="file" className="form-control" onChange={(e) => setFile(e.target.files[0])} required />
                    <small className="text-muted">Excel, Word, PDF, PowerPoint, ZIP, RAR, video, audio...</small>
                </div>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.VIDEO && (
                <>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Proveedor</label>
                            <select className="form-control form-select" value={video.providerId} onChange={(e) => setVideo({ ...video, providerId: Number(e.target.value) })}>
                                <option value={types.classroomVideoProvider.YOUTUBE}>YouTube</option>
                                <option value={types.classroomVideoProvider.VIMEO}>Vimeo</option>
                                <option value={types.classroomVideoProvider.OTRO}>Otro</option>
                            </select>
                        </div>
                        <div className="col-md-8 mb-3">
                            <label className="form-label">Título *</label>
                            <input type="text" className="form-control" value={video.title} onChange={(e) => setVideo({ ...video, title: e.target.value })} required />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">URL del video *</label>
                        <input type="url" className="form-control" value={video.videoUrl} onChange={(e) => setVideo({ ...video, videoUrl: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." required />
                    </div>
                </>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.FORUM && (
                <>
                    <div className="mb-3">
                        <label className="form-label">Título del foro *</label>
                        <input type="text" className="form-control" value={forum.title} onChange={(e) => setForum({ ...forum, title: e.target.value })} required />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Temática (opcional)</label>
                        <HtmlEditor value={forum.descriptionHtml} onChange={(value) => setForum({ ...forum, descriptionHtml: value })} />
                    </div>
                </>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.TASK && (
                <>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Tipo</label>
                            <select className="form-control form-select" value={task.taskTypeId} onChange={(e) => setTask({ ...task, taskTypeId: Number(e.target.value) })}>
                                <option value={types.classroomTaskTypes.TAREA}>Tarea</option>
                                <option value={types.classroomTaskTypes.ACTIVIDAD}>Actividad</option>
                                <option value={types.classroomTaskTypes.TALLER}>Taller</option>
                            </select>
                        </div>
                        <div className="col-md-8 mb-3">
                            <label className="form-label">Título *</label>
                            <input type="text" className="form-control" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} required />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Instrucciones</label>
                        <HtmlEditor value={task.instructionsHtml} onChange={(value) => setTask({ ...task, instructionsHtml: value })} />
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Fecha de apertura</label>
                            <input type="datetime-local" className="form-control" value={task.openDate} onChange={(e) => setTask({ ...task, openDate: e.target.value })} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Fecha de entrega</label>
                            <input type="datetime-local" className="form-control" value={task.dueDate} onChange={(e) => setTask({ ...task, dueDate: e.target.value })} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Nota máxima</label>
                            <input type="number" step="0.01" className="form-control" value={task.maxScore} onChange={(e) => setTask({ ...task, maxScore: e.target.value })} />
                        </div>
                    </div>
                    <div className="form-check mb-3">
                        <input type="checkbox" className="form-check-input" id="allowLate" checked={task.allowLateSubmission} onChange={(e) => setTask({ ...task, allowLateSubmission: e.target.checked })} />
                        <label className="form-check-label" htmlFor="allowLate">Aceptar entregas tardías</label>
                    </div>
                </>
            )}

            {esNuevo && tipo === types.classroomResourceTypes.EXAM && (
                <div className="mb-3">
                    <label className="form-label">Título del examen *</label>
                    <input type="text" className="form-control" value={examTitle} onChange={(e) => setExamTitle(e.target.value)} required />
                    <small className="text-muted">Después de crearlo podrás configurar el tiempo, las preguntas y la ponderación.</small>
                </div>
            )}

            {esNuevo && tipo !== types.classroomResourceTypes.CONTENT && tipo !== types.classroomResourceTypes.LINK && (
                <div className="mb-3">
                    <label className="form-label">Nombre a mostrar (opcional)</label>
                    <input type="text" className="form-control" name="name" value={placement.name} onChange={handlePlacementChange} placeholder="Si lo dejas vacío se usa el título" />
                </div>
            )}

            <div className="mb-3">
                <label className="form-label">Descripción corta (opcional)</label>
                <input type="text" className="form-control" name="description" value={placement.description} onChange={handlePlacementChange} />
            </div>

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label">Orden</label>
                    <input type="number" className="form-control" name="orden" value={placement.orden} onChange={handlePlacementChange} />
                </div>
                <div className="col-md-6 mb-3 d-flex align-items-end">
                    <div className="form-check">
                        <input type="checkbox" className="form-check-input" id="resourceVisible" name="isVisible" checked={placement.isVisible} onChange={handlePlacementChange} />
                        <label className="form-check-label" htmlFor="resourceVisible">Visible para los estudiantes</label>
                    </div>
                </div>
            </div>

            <button type="submit" className="btn btn-primary">Guardar</button>
        </form>
    );
};

export default ClassroomResourceForm;
